import {
  FETCH_TASKS_START,
  FETCH_TASKS_SUCCESS,
  FETCH_TASKS_ERROR,
  FETCH_PROJECTS_SUCCESS,
  SET_TASKS_SEARCH_QUERY,
  SET_TASKS_PROJECT_FILTER,
  SET_TASKS_STATUS_FILTER,
  SET_VIEW_MODE,
  TOGGLE_TASK_SELECTION,
  SELECT_ALL_TASKS,
  CLEAR_TASK_SELECTION,
  SET_SELECTED_TASK,
  TOGGLE_FILTERS,
  SET_PAGE,
  SET_PAGE_SIZE,
  LOAD_MORE_TASKS,
  OPEN_DRAWER,
  CLOSE_DRAWER,
  SET_DRAWER_SEARCH_QUERY,
  SET_TASKS_TASK_TYPE_FILTER,
  SET_TASKS_NUMBER_FILTER,
  SET_TASKS_CTI_FILTER,
  SET_TASKS_ORDER_FILTER,
  SET_TASKS_PROJECT_ID_FILTER,
  SET_TASKS_SOW_FILTER,
  SET_TASKS_DISTRICT_FILTER,
  SET_TASKS_IS_HOLD_FILTER,
  SET_TASKS_PINCODES_FILTER,
  SET_TASKS_HRVID_OR_EMAIL_FILTER,
  SET_TASKS_ALLOC_PARTNER_TYPE_FILTER,
  RESET_TASKS_FILTERS,
} from "../../constants/ActionTypes";
import { groupTasksByTimeStatus } from "../../routes/ProjectManagementTool/Tasks/NewTasks/utils/dataTransform";

const INIT_STATE = {
  // Data
  tasks: [],
  projectList: [],
  counts: {},
  newTicketTasks: [], // For future use
  allocateBackupPartnerTasks: [], // Backup partner allocation tasks

  // Filtering & Grouping
  filteredTasks: [],
  groupedTasks: {
    active: [],
    overdue: [],
    upcoming: [],
    other: [],
  },

  // Filters
  statusFilter: "all",
  projectFilter: "",
  searchQuery: "",
  taskTypeFilter: "",
  numberFilter: "",
  ctiFilter: "",
  orderFilter: "",
  projectIdFilter: "",
  sowFilter: "",
  districtFilter: "",
  isHoldFilter: "",
  pincodesFilter: [],
  hrvidOrEmailFilter: "",
  allocPartnerTypeFilter: "",

  // UI State
  viewMode: "card", // 'card' or 'list'
  selectedTasks: [], // Array of task IDs
  selectedTask: null,
  showFilters: false,

  // Loading & Pagination
  loading: false,
  error: null,
  currentPage: 1,
  pageSize: 50,
  totalCount: 0,
  hasMore: true,

  // Drawer State
  drawerVisible: false,
  drawerContent: {
    title: "",
    tasks: [],
    color: "",
  },
  drawerSearchQuery: "",
};

// Helper function to filter tasks
// NOTE: text search is not applied here - the server filters by the search box
// (see buildTaskListParams), so every task returned by the API is kept.
const filterTasks = (tasks, statusFilter, projectFilter) => {
  if (tasks.length === 0) {
    return [];
  }

  return tasks.filter((task) => {
    // Status filter
    const matchesStatus =
      statusFilter === "all" ||
      task.status.toLowerCase() === statusFilter.toLowerCase();

    // Project filter
    const matchesProject =
      !projectFilter ||
      (task.originalData &&
        task.originalData.project &&
        task.originalData.project.id.toString() === projectFilter.toString());

    return matchesStatus && matchesProject;
  });
};

export default (state = INIT_STATE, action) => {
  switch (action.type) {
    case FETCH_TASKS_START:
      return {
        ...state,
        loading: true,
        error: null,
      };

    case FETCH_TASKS_SUCCESS: {
      const {
        tasks,
        newTicketTasks,
        allocateBackupPartnerTasks,
        counts,
        totalCount,
        append,
        page,
        pageSize,
      } = action.payload;

      // Update tasks (append or replace)
      const updatedTasks = append ? [...state.tasks, ...tasks] : tasks;

      // Update new ticket tasks (only on first page, not when appending)
      const updatedNewTicketTasks = append
        ? state.newTicketTasks
        : newTicketTasks || [];

      // Update backup partner tasks (only on first page, not when appending)
      const updatedAllocateBackupPartnerTasks = append
        ? state.allocateBackupPartnerTasks
        : allocateBackupPartnerTasks || [];

      // Filter and group tasks
      const filtered = filterTasks(
        updatedTasks,
        state.statusFilter,
        state.projectFilter
      );
      const grouped = groupTasksByTimeStatus(filtered);

      // Calculate hasMore
      const hasMoreData =
        updatedTasks.length < totalCount && tasks.length === pageSize;

      return {
        ...state,
        tasks: updatedTasks,
        newTicketTasks: updatedNewTicketTasks,
        allocateBackupPartnerTasks: updatedAllocateBackupPartnerTasks,
        filteredTasks: filtered,
        groupedTasks: grouped,
        counts: append ? state.counts : counts, // Only update counts on first page
        totalCount,
        currentPage: page,
        pageSize,
        hasMore: hasMoreData,
        loading: false,
        error: null,
      };
    }

    case FETCH_TASKS_ERROR:
      return {
        ...state,
        loading: false,
        error: action.payload,
        // Clear tasks on error if not appending
        ...(action.payload.append
          ? {}
          : {
              tasks: [],
              filteredTasks: [],
              groupedTasks: {
                active: [],
                overdue: [],
                upcoming: [],
                other: [],
              },
              counts: {},
              totalCount: 0,
            }),
        hasMore: false,
      };

    case FETCH_PROJECTS_SUCCESS:
      return {
        ...state,
        projectList: action.payload,
      };

    case SET_TASKS_STATUS_FILTER: {
      const statusFilter = action.payload;
      const filtered = filterTasks(
        state.tasks,
        statusFilter,
        state.projectFilter
      );
      const grouped = groupTasksByTimeStatus(filtered);

      return {
        ...state,
        statusFilter,
        filteredTasks: filtered,
        groupedTasks: grouped,
        currentPage: 1,
        hasMore: true,
      };
    }

    case SET_TASKS_PROJECT_FILTER: {
      const projectFilter = action.payload || "";
      const filtered = filterTasks(
        state.tasks,
        state.statusFilter,
        projectFilter
      );
      const grouped = groupTasksByTimeStatus(filtered);

      return {
        ...state,
        projectFilter,
        filteredTasks: filtered,
        groupedTasks: grouped,
        currentPage: 1,
        hasMore: true,
      };
    }

    case SET_TASKS_SEARCH_QUERY:
      // The list is refetched from the server for the new text (NewTasks.js),
      // so loaded tasks are left alone here.
      return {
        ...state,
        searchQuery: action.payload,
        currentPage: 1,
        hasMore: true,
      };

    case SET_VIEW_MODE:
      return {
        ...state,
        viewMode: action.payload,
      };

    case TOGGLE_TASK_SELECTION: {
      const taskId = action.payload;
      const selectedSet = new Set(state.selectedTasks);
      if (selectedSet.has(taskId)) {
        selectedSet.delete(taskId);
      } else {
        selectedSet.add(taskId);
      }
      return {
        ...state,
        selectedTasks: Array.from(selectedSet),
      };
    }

    case SELECT_ALL_TASKS: {
      const selectedSet = new Set(state.selectedTasks);
      const allSelected = selectedSet.size === state.filteredTasks.length;
      const newSelected = allSelected
        ? []
        : state.filteredTasks.map((t) => t.id);
      return {
        ...state,
        selectedTasks: newSelected,
      };
    }

    case CLEAR_TASK_SELECTION:
      return {
        ...state,
        selectedTasks: [],
      };

    case SET_SELECTED_TASK:
      return {
        ...state,
        selectedTask: action.payload,
      };

    case TOGGLE_FILTERS:
      return {
        ...state,
        showFilters: !state.showFilters,
      };

    case SET_PAGE:
      return {
        ...state,
        currentPage: action.payload,
      };

    case SET_PAGE_SIZE:
      return {
        ...state,
        pageSize: action.payload,
        currentPage: 1,
        hasMore: true,
      };

    case LOAD_MORE_TASKS:
      return {
        ...state,
        currentPage: state.currentPage + 1,
      };

    case OPEN_DRAWER:
      return {
        ...state,
        drawerVisible: true,
        drawerContent: action.payload,
        drawerSearchQuery: "", // Reset search when opening drawer
      };

    case CLOSE_DRAWER:
      return {
        ...state,
        drawerVisible: false,
        drawerContent: {
          title: "",
          tasks: [],
          color: "",
        },
        drawerSearchQuery: "",
      };

    case SET_DRAWER_SEARCH_QUERY:
      return {
        ...state,
        drawerSearchQuery: action.payload,
      };

    case SET_TASKS_TASK_TYPE_FILTER:
      return {
        ...state,
        taskTypeFilter: action.payload,
        currentPage: 1,
        hasMore: true,
      };

    case SET_TASKS_NUMBER_FILTER:
      return {
        ...state,
        numberFilter: action.payload,
        currentPage: 1,
        hasMore: true,
      };

    case SET_TASKS_CTI_FILTER:
      return {
        ...state,
        ctiFilter: action.payload,
        currentPage: 1,
        hasMore: true,
      };

    case SET_TASKS_ORDER_FILTER:
      return {
        ...state,
        orderFilter: action.payload,
        currentPage: 1,
        hasMore: true,
      };

    case SET_TASKS_PROJECT_ID_FILTER:
      return {
        ...state,
        projectIdFilter: action.payload,
        currentPage: 1,
        hasMore: true,
      };

    case SET_TASKS_SOW_FILTER:
      return {
        ...state,
        sowFilter: action.payload,
        currentPage: 1,
        hasMore: true,
      };

    case SET_TASKS_DISTRICT_FILTER:
      return {
        ...state,
        districtFilter: action.payload,
        currentPage: 1,
        hasMore: true,
      };

    case SET_TASKS_IS_HOLD_FILTER:
      return {
        ...state,
        isHoldFilter: action.payload,
        currentPage: 1,
        hasMore: true,
      };

    case SET_TASKS_PINCODES_FILTER:
      return {
        ...state,
        pincodesFilter: action.payload,
        currentPage: 1,
        hasMore: true,
      };

    case SET_TASKS_HRVID_OR_EMAIL_FILTER:
      return {
        ...state,
        hrvidOrEmailFilter: action.payload,
        currentPage: 1,
        hasMore: true,
      };

    case SET_TASKS_ALLOC_PARTNER_TYPE_FILTER:
      return {
        ...state,
        allocPartnerTypeFilter: action.payload,
        currentPage: 1,
        hasMore: true,
      };

    case RESET_TASKS_FILTERS:
      return {
        ...state,
        statusFilter: "all",
        projectFilter: "",
        searchQuery: "",
        taskTypeFilter: "",
        numberFilter: "",
        ctiFilter: "",
        orderFilter: "",
        projectIdFilter: "",
        sowFilter: "",
        districtFilter: "",
        isHoldFilter: "",
        pincodesFilter: [],
        hrvidOrEmailFilter: "",
        allocPartnerTypeFilter: "",
        currentPage: 1,
        hasMore: true,
      };

    default:
      return state;
  }
};
